/**
 * FE-T02 — useAdminSubDelegates hooks
 *
 * Thin React Query wrappers around `adminSubDelegatesApi`. Invalidation
 * keys mirror `useAdminUsers` so list refetches after every mutation.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { adminSubDelegatesApi } from '../services/adminSubDelegatesApi';
import type {
  CreateSubDelegatePayload,
  InviteSubDelegateResponse,
  RevokeRelationshipResponse,
  SubDelegateListResponse,
  UpdatePermissionsPayload,
  UpdatePermissionsResponse,
  UpdateSubDelegateLimitResponse,
} from '../types/sub-delegate.type';

export const subDelegateKeys = {
  all: ['admin', 'sub-delegates'] as const,
  list: (ownerId: string) => [...subDelegateKeys.all, ownerId] as const,
};

export function useAdminSubDelegates(
  ownerId: string | null,
): UseQueryResult<SubDelegateListResponse, Error> {
  return useQuery({
    queryKey: subDelegateKeys.list(ownerId ?? ''),
    queryFn: () => adminSubDelegatesApi.listSubDelegates(ownerId as string),
    enabled: !!ownerId,
    staleTime: 30_000,
  });
}

export function useCreateAdminSubDelegate(
  ownerId: string,
): UseMutationResult<InviteSubDelegateResponse, Error, CreateSubDelegatePayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      adminSubDelegatesApi.createSubDelegate(ownerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}

export function useResendAdminSubDelegate(
  ownerId: string,
): UseMutationResult<InviteSubDelegateResponse, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pendingId) =>
      adminSubDelegatesApi.resendSubDelegateInvite(ownerId, pendingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}

export function useCancelAdminSubDelegate(
  ownerId: string,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pendingId) =>
      adminSubDelegatesApi.cancelSubDelegateInvite(ownerId, pendingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}

export function useUpdateAdminSubDelegatePermissions(
  ownerId: string,
): UseMutationResult<UpdatePermissionsResponse, Error, UpdatePermissionsPayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ relationshipId, permissions }) =>
      adminSubDelegatesApi.updateSubDelegatePermissions(
        ownerId,
        relationshipId,
        permissions,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}

export function useUpdateAdminSubDelegateLimit(
  ownerId: string,
): UseMutationResult<UpdateSubDelegateLimitResponse, Error, number | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limit) =>
      adminSubDelegatesApi.updateSubDelegateLimit(ownerId, limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}

export function useRevokeAdminSubDelegate(
  ownerId: string,
): UseMutationResult<RevokeRelationshipResponse, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (relationshipId) =>
      adminSubDelegatesApi.revokeSubDelegateRelationship(ownerId, relationshipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subDelegateKeys.list(ownerId) });
    },
  });
}
